import LanguageErrorListener from './LanguageErrorListener';
import { InputStream, CommonTokenStream } from 'antlr4';
import { LanguageLexer } from '../Language/LanguageLexer';
import { LanguageParser } from '../Language/LanguageParser';
import { findClosingPair, findLineColumn, getWordByPosition } from '../../editor.utils';
import _ from 'lodash';

/**
 * Verifica por erros de sintaxe com base na análise no Antlr.
 * @param {string} input Bloco de texto
 * @param {Array} supportConstants Constantes de suporte para a fórmula, dados da entidade sendo tranalhada.
 */
export const getSyntaxErrors = (input, supportConstants) => {
  const errorListener = new LanguageErrorListener();

  const chars = new InputStream(input);
  const lexer = new LanguageLexer(chars);

  lexer.removeErrorListeners();
  lexer.addErrorListener(errorListener);

  const commonToken = new CommonTokenStream(lexer);
  const parser = new LanguageParser(commonToken);

  parser.removeErrorListeners();
  parser.addErrorListener(errorListener);

  parser.buildParseTrees = true;
  parser.rule_set();

  // Erros na declaração e utilização de variáveis.
  const varKey = parser.symbolicNames.indexOf('VAR');
  const openBracket = parser.symbolicNames.indexOf('LBRACKET');
  const identifierKey = parser.symbolicNames.indexOf('IDENTIFIER');
  const constantKey = parser.symbolicNames.indexOf('VAR_PRIMARY');
  const dotKey = parser.symbolicNames.indexOf('DOT');
  const commaKey = parser.symbolicNames.indexOf('COMMA');
  const lambdaKey = parser.symbolicNames.indexOf('LAMBDA_SIGN');

  const roteiroIdentifier = '@Roteiro';
  const variavelIdentifier = '@Variavel';
  const origemIdentifier = '@Origem';

  let declarationErrors = [];
  let declaredVars = [];
  let notDeclaredVars = [];
  const tokens = commonToken.getTokens(0, commonToken.index - 1, null) || [];
  tokens
    .filter((item) => item.type === identifierKey)
    .forEach((element) => {
      const index = tokens.indexOf(element);
      const previous = tokens[index - 1];
      const next = tokens[index + 1];
      if (previous && previous.type === varKey) {
        if (declaredVars.find((d) => d.text === element.text)) declarationErrors.push(element);
        else declaredVars.push(element);
      } else if (next && next.type === lambdaKey) {
        if (!declaredVars.find((d) => d.text === element.text)) declaredVars.push(element);
      } else if (previous && (previous.type === dotKey || previous.type === commaKey)) {
      } else if (!declaredVars.find((d) => d.text === element.text)) {
        notDeclaredVars.push(element);
      }
    });

  let errors = [];
  declarationErrors.forEach((d) => {
    const lineColumn = findLineColumn(input, d.start, d.stop);
    errors.push({ ...lineColumn, mensagem: `Variável '${d.text}' já foi declarada` });
  });

  notDeclaredVars.forEach((d) => {
    const lineColumn = findLineColumn(input, d.start, d.stop);

    const lines = input.split('\n');
    const w = getWordByPosition(lines[d.line - 1], d.column);

    const regexAcentuacao = /^[0-9]|[\u00C0-\u024F\u1E00-\u1EFF]+/;
    const mensagem = w.word.match(regexAcentuacao)
      ? 'Identificador inválido: Um identificador deve iniciar com uma letra ou sublinhado, e não deve conter caracteres especiais e/ou acentuação'
      : `Variável '${w.word}' não foi declarada`;
    lineColumn.coluna = w.index;

    errors.push({ ...lineColumn, mensagem: mensagem });
  });

  tokens
    .filter((t) => t.type === constantKey && t.text != origemIdentifier)
    .forEach((t) => {
      const supportConstant = supportConstants.find((sc) => `@${sc.Nome}` === t.text);

      if (supportConstant) {
        const nextIndex = tokens.indexOf(t) + 1;
        const nextToken = tokens[nextIndex];

        // Verifica se o próximo token é '.', ou seja, para acessar uma propriedade de um objeto.
        if (nextToken && nextToken.type === dotKey) {
          const nextToken2 = tokens[nextIndex + 1];

          // Se o próximo token é um identificador, verifica se está na lista de constantes de suporte.
          if (nextToken2 && nextToken2.type === identifierKey) {
            if (!supportConstant.Colunas.find((c) => c.Nome === nextToken2.text)) {
              const lineColumn = findLineColumn(input, nextToken2.start, nextToken2.stop);
              errors.push({
                ...lineColumn,
                mensagem: `Propriedade '${nextToken2.text}' não encontrada na constante global '${t.text}'`,
              });
            }
            // Caso possua um '.' e depois não haja um identicador, então a propriedade não foi fornecida
          } else {
            const lineColumn = findLineColumn(
              input,
              nextToken2 ? nextToken2.start : nextToken.start,
              nextToken2 ? nextToken2.stop : nextToken.stop
            );
            errors.push({
              ...lineColumn,
              mensagem: `Propriedade não fornecida para a constante global "${t.text}"`,
            });
          }
          // Verifica se o próximo token é '[', para acessar uma propriedade em uma lista de objetos.
        } else if (nextToken && nextToken.type === openBracket) {
          const closingPair = findClosingPair(input, nextToken.start, ']');
          if (closingPair >= 0) {
            const nextIndex2 = tokens.findIndex((cp) => cp.start === closingPair) + 2;
            const nextToken2 = tokens[nextIndex2];
            if (nextToken2 && nextToken2.type === identifierKey) {
              if (supportConstant.Lista === false) {
                const lineColumn = findLineColumn(input, t.start, t.stop);
                errors.push({ ...lineColumn, mensagem: `Constante global "${t.text}" não é lista` });
              } else if (!supportConstant.Colunas.find((sc) => sc.Nome === nextToken2.text)) {
                const lineColumn = findLineColumn(input, nextToken2.start, nextToken2.stop);
                errors.push({
                  ...lineColumn,
                  mensagem: `Propriedade '${nextToken2.text}' não encontrada na constante global "${t.text}"`,
                });
              }
            }
          }
        }
      } else {
        const lineColumn = findLineColumn(input, t.start, t.stop);
        errors.push({ ...lineColumn, mensagem: `Constante global '${t.text}' não encontrada` });
      }
    });

  return errorListener.errors.concat(errors);
};

/**
 * Busca por identificadores através da análise provida pelo Antlr4.
 * @param {string} input Bloco de texto
 */
export const getIdentifiers = (input) => {
  const chars = new InputStream(input);
  const lexer = new LanguageLexer(chars);
  lexer.removeErrorListeners();

  const commonToken = new CommonTokenStream(lexer);
  const parser = new LanguageParser(commonToken);
  parser.removeErrorListeners();

  parser.buildParseTrees = true;
  parser.rule_set();

  const identifierKey = parser.symbolicNames.indexOf('IDENTIFIER');
  const varKey = parser.symbolicNames.indexOf('VAR');

  const tokens = commonToken.getTokens(0, commonToken.index - 1, null) || [];

  const identifierTokens = tokens
    .filter((item) => {
      if (item.type !== identifierKey) return false;
      const previousIndex = tokens.indexOf(item) - 1;
      const previousToken = tokens[previousIndex];

      return previousToken && previousToken.type === varKey;
    })
    .map((item) => item.text);

  return [...new Set(identifierTokens)];
};

export const getExternalConstants = (input, variaveis, formulas) => {
  const chars = new InputStream(input);
  const lexer = new LanguageLexer(chars);
  lexer.removeErrorListeners();

  const commonToken = new CommonTokenStream(lexer);
  const parser = new LanguageParser(commonToken);
  parser.removeErrorListeners();

  parser.buildParseTrees = true;
  parser.rule_set();

  const countIfKey = parser.symbolicNames.indexOf('COUNT_IF');
  const sumIfKey = parser.symbolicNames.indexOf('SUM_IF');
  const lengthKey = parser.symbolicNames.indexOf('LENGTH');
  const constantKey = parser.symbolicNames.indexOf('VAR_PRIMARY');
  const dotKey = parser.symbolicNames.indexOf('DOT');
  const identifierKey = parser.symbolicNames.indexOf('IDENTIFIER');
  const lbracketKey = parser.symbolicNames.indexOf('LBRACKET');
  const rbracketKey = parser.symbolicNames.indexOf('RBRACKET');
  const lambdaSignKey = parser.symbolicNames.indexOf('LAMBDA_SIGN');
  const variavelIdentifier = '@Variavel';
  const roteiroIdentifier = '@Roteiro';

  const tokens = commonToken.getTokens(0, commonToken.index - 1, null) || [];

  const externalConstants = tokens
    .filter((item) => item.type === constantKey && item.text === variavelIdentifier)
    .map((item) => {
      const nextIndex = tokens.indexOf(item) + 1;
      const nextToken = tokens[nextIndex];

      if (nextToken && nextToken.type === dotKey) {
        const nextToken2 = tokens[nextIndex + 1];

        if (nextToken2 && nextToken2.type === identifierKey) return variaveis.find((e) => e.Nome == nextToken2.text);
      }

      return null;
    });
  let roteiroConstants = tokens
    .filter((item) => item.type === constantKey && item.text === roteiroIdentifier)
    .map((item) => {
      const nextIndex = tokens.indexOf(item) + 1;
      const nextToken = tokens[nextIndex];

      if (nextToken && nextToken.type === dotKey) {
        const nextToken2 = tokens[nextIndex + 1];

        if (nextToken2 && nextToken2.type === identifierKey)
          return { ...formulas.find((e) => e.Nome == nextToken2.text) };
      }

      return null;
    });

  roteiroConstants = _.uniqBy(roteiroConstants, 'Nome');

  tokens
    .filter((item) => item.type === constantKey && item.text === roteiroIdentifier)
    .forEach((item) => {
      const nextIndex = tokens.indexOf(item) + 1;
      const nextToken = tokens[nextIndex];

      if (nextToken && nextToken.type === dotKey) {
        const nextToken1 = tokens[nextIndex + 1];

        if (nextToken1 && nextToken1.type === identifierKey) {
          const nextToken2 = tokens[nextIndex + 2];

          if (nextToken2 && nextToken2.type == lbracketKey) {
            const nextToken4 = tokens[nextIndex + 4];
            const nextToken5 = tokens[nextIndex + 5];
            const nextToken6 = tokens[nextIndex + 6];

            if (
              nextToken4 &&
              nextToken4.type === rbracketKey &&
              nextToken5 &&
              nextToken5.type == dotKey &&
              nextToken6 &&
              nextToken6.type === identifierKey
            ) {
              const roteiro = roteiroConstants.find((r) => r.Nome == nextToken1.text);
              roteiro.Propriedades = Array.isArray(roteiro.Propriedades)
                ? roteiro.Propriedades.includes(nextToken6.text)
                  ? roteiro.Propriedades
                  : [...roteiro.Propriedades, nextToken6.text]
                : [nextToken6.text];
              return;
            }
          }

          if (nextToken2 && nextToken2.type == dotKey) {
            const nextToken3 = tokens[nextIndex + 3];

            if (nextToken3 && nextToken3.type == identifierKey) {
              const roteiro = roteiroConstants.find((r) => r.Nome == nextToken1.text);
              roteiro.Propriedades = Array.isArray(roteiro.Propriedades)
                ? roteiro.Propriedades.includes(nextToken3.text)
                  ? roteiro.Propriedades
                  : [...roteiro.Propriedades, nextToken3.text]
                : [nextToken3.text];
            }
          }
        }
      }
    });

  // funções
  tokens
    .filter((item) => item.type === constantKey && item.text === roteiroIdentifier)
    .forEach((item) => {
      const nextIndex = tokens.indexOf(item) + 1;
      const nextToken = tokens[nextIndex];

      if (nextToken && nextToken.type === dotKey) {
        const nextToken2 = tokens[nextIndex + 1];

        if (nextToken2 && nextToken2.type === identifierKey) {
          const lastToken2 = tokens[nextIndex - 3];

          if (lastToken2 && lastToken2.type === countIfKey) {
            const lambdaIdentifier = tokens[nextIndex + 5];
            const nextToken2 = tokens[nextIndex + 1];

            const closingIndex = getClosingParen(tokens, nextIndex - 2);
            const tokenRange = tokens.slice(nextIndex - 2, closingIndex);
            const lambdaSignIndex = tokenRange.findIndex((t) => t.type === lambdaSignKey);
            const properties = tokenRange
              .slice(lambdaSignIndex)
              .filter((t) => t.type == identifierKey && t.text == lambdaIdentifier.text)
              .map((t) => {
                const lambdaIdentifierIndex = tokens.indexOf(t);
                const lambdaProperty = tokens[lambdaIdentifierIndex + 2];

                return lambdaProperty?.text;
              });

            const roteiro = roteiroConstants.find((r) => r.Nome == nextToken2.text);

            roteiro.Propriedades = [...new Set([...roteiro.Propriedades, ...properties])];
            return;
          }

          if (lastToken2 && lastToken2.type === sumIfKey) {
            const lambdaIdentifier = tokens[nextIndex + 5];
            const nextToken2 = tokens[nextIndex + 1];

            const closingIndex = getClosingParen(tokens, nextIndex - 2);
            const tokenRange = tokens.slice(nextIndex - 2, closingIndex);
            const lambdaSignIndex = tokenRange.findIndex((t) => t.type === lambdaSignKey);
            const properties = tokenRange
              .slice(lambdaSignIndex)
              .filter((t) => t.type == identifierKey && t.text == lambdaIdentifier.text)
              .map((t) => {
                const lambdaIdentifierIndex = tokens.indexOf(t);
                const lambdaProperty = tokens[lambdaIdentifierIndex + 2];

                return lambdaProperty?.text;
              });

            const roteiro = roteiroConstants.find((r) => r.Nome == nextToken2.text);

            roteiro.Propriedades = [...new Set([...roteiro.Propriedades, ...properties])];
            return;
          }

          if (lastToken2 && lastToken2.type === lengthKey) {
            const nextToken2 = tokens[nextIndex + 1];

            const roteiro = roteiroConstants.find((r) => r.Nome == nextToken2.text);
            roteiro.Propriedades = roteiro.Propriedades || ['Id'];
            return;
          }
        }
      }

      return null;
    });

  return { Roteiro: roteiroConstants, Variavel: [...new Set(externalConstants)] };
};

const getClosingParen = (tokens, openParenIndex) => {
  const closingParen = tokens.slice(openParenIndex).find((x) => x.text === ')');
  const openParens = tokens.slice(openParenIndex, closingParen).filter((x) => x.text === '(').length;
  const closeParens = tokens.slice(openParenIndex, closingParen).filter((x) => x.text === ')').length;
  if (openParens == closeParens) {
    return closingParen?.tokenIndex || 0;
  } else {
    return getClosingParen(tokens.slice(openParenIndex).find((x) => x.text === '('));
  }
};
